'use client';

import { useMutation } from '@apollo/client/react';
import { isApolloError, getErrorCauseMessage } from '@/src/utils/error-handler';
import { PortalModal, usePortalContext } from '@cloai/uikit';
import { useTranslations } from 'next-intl';
import Image from 'next/image';
import { FC } from 'react';
import { toast } from 'react-toastify';
import { twMerge } from 'tailwind-merge';

import { PORTALS } from '@/.constants/portals';
import { INPAINT_IMAGE } from '@/.graphql';
import { Header } from '@/src/components/Header';
import { MaskBlock } from '@/src/components/mask-block/MaskBlock';
import {
  useStyleContext,
  useGTMContext,
  useAutoMaskContext,
  useMobileContext,
} from '@/src/providers';
import { convertMask } from '@/src/utils/converter/convertMask';
import { createFile } from '@/src/utils/converter/createFile';

const styles = {
  portal: '!h-dvh !max-h-dvh !rounded-none !bg-none !bg-fill-menu !p-0',
  head: 'absolute top-0 left-0 right-0 z-20 py-2 px-5 flex justify-center bg-fill-menu dark:bg-fill-menu bg-fill-menuBg',
  wrapper: 'shrink-0 bg-fill-menu p-5 pt-12',
};

export const InpaintModePortal: FC = () => {
  const t = useTranslations('imageModal');
  const { genderMode } = useStyleContext();
  const { inpaintFile } = useAutoMaskContext();
  const sendGTM = useGTMContext();
  const { closePortal, openPortal } = usePortalContext();
  const [inpaintImage, { loading }] = useMutation(INPAINT_IMAGE, {
    context: {
      headers: {
        'apollo-require-preflight': true,
      },
    },
  });
  const { isMobiles } = useMobileContext();

  const handleClickGenerate = async (prompt: string): Promise<void> => {
    try {
      let mask = null;

      if (inpaintFile.mask) {
        const base64Data = convertMask(inpaintFile.mask);
        mask = await createFile(base64Data, 'mask.png');
      }

      sendGTM({
        event: 'Generate',
        type: null,
        block: 'Inpaint',
        category_name: null,
        name: null,
        category_position: null,
        vitrina_position: null,
        getSource: 'Меню',
        gender: genderMode,
      });

      const { data } = await inpaintImage({
        variables: {
          image: inpaintFile.file,
          mask: mask,
          prompt,
        },
      });

      if (!data) {
        throw new Error('No data');
      }

      const queueTime = data.inpaintImage.queueTime;

      toast(
        <span>
          {t.rich('successMessage', {
            queueTime,
            b: (chunks: React.ReactNode) => <b>{chunks}</b>,
          })}
        </span>,
        {
          position: 'bottom-center',
          icon: (
            <Image
              width={16}
              height={18}
              src="/icons/check.svg"
              alt="check"
              className="w-4 h-4"
            />
          ),
          theme: 'dark',
          style: {
            backgroundColor: '#3D3E41',
            color: '#FFFFFF',
            fontSize: '14px',
            fontWeight: '500',
            borderRadius: '12px',
            padding: '16px',
            bottom: '32px',
            maxWidth: 'calc(100% - 40px)',
            alignItems: 'start',
          },
        },
      );

      sendGTM({
        event: 'Generation_complete',
        type: null,
        block: 'Inpaint',
        category_name: null,
        name: null,
        category_position: null,
        vitrina_position: null,
        getSource: 'Меню',
        gender: genderMode,
      });
    } catch (err) {
      const error = err as Error;

      if (isApolloError(error)) {
        const causeMessage = getErrorCauseMessage(error);
        if (causeMessage === 'not_enough_image_generations') {
          closePortal();
          openPortal(PORTALS.TopUpBalance);
        } else {
          toast.error(causeMessage || error.message);
        }
      } else {
        console.error(error);
      }

      sendGTM({
        event: 'Generation_error',
        type: null,
        block: 'Inpaint',
        category_name: null,
        name: null,
        category_position: null,
        vitrina_position: null,
        getSource: 'Меню',
        gender: genderMode,
      });
    }
  };

  const handleClosePortal = () => {
    closePortal();
  };

  return (
    <PortalModal
      portalId={PORTALS.InpaintMode}
      className={styles.portal}
      noClose
    >
      <div className="h-full relative">
        <div className={twMerge(styles.head, isMobiles && 'pt-[100px]')}>
          <Header isCloseButton onClickCloseButton={handleClosePortal} />
        </div>
        <div
          className={twMerge(
            'h-full overflow-y-auto',
            isMobiles && 'pt-[100px]',
          )}
        >
          <div className={styles.wrapper}>
            <MaskBlock
              imageId={inpaintFile.id}
              image={inpaintFile.fileUrl}
              onClickGenerate={handleClickGenerate}
              loading={loading}
            />
          </div>
        </div>
      </div>
    </PortalModal>
  );
};
